import React from "react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { formatBillingMoney } from "../../utils/billingCalculation.js";
import { approx, kmLabel, timeLabel, tonsLabel } from "../../utils/billingCalculationDays.js";
import { groupInvoiceRows, normalizedSearch } from "../../utils/billingInvoiceGroups.js";

// Small drafts stay open as before; a long period opens collapsed, one line per day sheet.
const OPEN_UP_TO_ROWS = 20;
const dayLabel = (value, options) => new Intl.DateTimeFormat("fr-CA", { ...options, timeZone: "UTC" }).format(new Date(`${value}T12:00:00Z`));
const hoursLabel = (hours) => hours == null ? "—" : timeLabel(hours * 3600);
const plural = (count, word) => `${count} ${word}${count > 1 ? "s" : ""}`;
const joined = (...parts) => parts.filter(Boolean).join(" · ");
const BUTTON = "inline-flex h-6 items-center gap-1 rounded-[4px] border px-2 text-[11px] font-bold focus-visible:outline-2 focus-visible:outline-accent";
const OFF = "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900";
const ON = "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong";

/** Grouping, search, day filter and open day sheets shared by the summary and the lines table.
 * `sharedGuide` : état « flèche déjà montrée » tenu par l'appelant quand le relevé se remonte (enregistrement, mise à jour). */
export function useInvoiceLineGroups(rows, rawLines, sharedGuide) {
  const groups = React.useMemo(() => groupInvoiceRows(rows, rawLines), [rows, rawLines]);
  const [query, setQuery] = React.useState("");
  const [day, setDay] = React.useState(null);
  const [openDays, setOpenDays] = React.useState(() => new Set());
  // Conservés avec le relevé, même lorsque le tableau « Par trajet » est démonté.
  const ownGuide = React.useRef(false);
  const guideStarted = sharedGuide || ownGuide;
  const guideControls = React.useRef(null);
  const collapsed = rows.length > OPEN_UP_TO_ROWS;
  // Day sheets whose state differs from the default (open or collapsed).
  const [toggled, setToggled] = React.useState(() => new Set());
  const needle = normalizedSearch(query);
  const days = groups.days.filter((item) => !day || item.date === day)
    .map((item) => ({ ...item, tourCount: item.tours.length, tours: needle ? item.tours.filter((tour) => tour.search.includes(needle)) : item.tours }))
    .filter((item) => item.tours.length);
  return {
    ...groups, enabled: groups.tourCount > 0, collapsed, days, allDays: groups.days, query, setQuery, day, guideStarted, guideControls,
    setDay: (value) => {
      setDay(value);
      if (value) setOpenDays((previous) => new Set(previous).add(value));
    },
    isDayOpen: (key) => Boolean(needle) || openDays.has(key),
    toggleDay: (key) => setOpenDays((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    }),
    isOpen: (key) => Boolean(needle) || collapsed === toggled.has(key),
    toggle: (key) => setToggled((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    }),
    setAll: (open) => {
      setOpenDays(open ? new Set(groups.days.map((item) => item.key)) : new Set());
      setToggled(open === collapsed ? new Set(groups.days.flatMap((item) => item.tours.map((tour) => tour.key))) : new Set());
    },
  };
}

/** Les outils qui gardent une longue période lisible ; les heures du relevé sont dans « Comprendre le montant », juste au-dessus. */
export function InvoiceLinesOverview({ view, currency, actions = null, byOrder = false }) {
  if (!view.enabled) return null;
  const anyOpen = view.days.some((item) => view.isDayOpen(item.key));
  return <section aria-label="Résumé des lignes" className="mb-2 grid gap-1.5">
    <div className="flex flex-wrap items-center gap-1.5">
      <label className="relative min-w-[14rem] flex-1">
        <span className="sr-only">Rechercher une commande, une ferme, une meunerie ou un chauffeur</span>
        <Search size={13} aria-hidden="true" className="pointer-events-none absolute left-2 top-1.5 text-slate-400" />
        <input type="search" value={view.query} onChange={(event) => view.setQuery(event.target.value)} placeholder="Commande, ferme, meunerie ou chauffeur…"
          className="h-6 w-full rounded-[4px] border border-slate-300 bg-white py-0 pl-7 pr-2 text-xs text-slate-800 focus-visible:outline-2 focus-visible:outline-accent" />
      </label>
      <div ref={view.guideControls} className="flex flex-wrap items-center gap-1.5">
      <div role="group" aria-label="Journées" className="flex flex-wrap gap-1">
        <button type="button" aria-pressed={!view.day} className={`${BUTTON} ${view.day ? OFF : ON}`} onClick={() => view.setDay(null)}>Toutes</button>
        {view.allDays.map((item) => <button key={item.key} type="button" aria-pressed={view.day === item.date} className={`${BUTTON} ${view.day === item.date ? ON : OFF}`}
          onClick={() => view.setDay(view.day === item.date ? null : item.date)}>{dayLabel(item.date, { weekday: "short", day: "numeric", month: "short" })}<span className="font-semibold opacity-80 tabular-nums">{formatBillingMoney(item.amount, currency)}</span></button>)}
      </div>
      {byOrder ? null : <button type="button" className={`${BUTTON} ${OFF}`} onClick={() => view.setAll(!anyOpen)}>{anyOpen ? "Tout replier" : "Tout déplier"}</button>}
      {actions}
      </div>
    </div>
  </section>;
}

// Billed times confirmed by Geotab (`undefined` while reading, null without a Geotab day); opens the « Preuve GPS ».
function GpsChip({ proof, selected, onClick }) {
  const label = proof === undefined ? "GPS…" : proof?.total ? `GPS ${proof.ok ? "✓ " : ""}${proof.checked}/${proof.total}` : "GPS —";
  const tone = !proof?.total ? "border-slate-300 bg-white text-slate-500" : proof.ok ? "border-accent bg-white text-accent" : "border-[#a86a24] bg-[#a86a24] text-white";
  return <button type="button" aria-pressed={selected} onClick={onClick} aria-label={`Preuve GPS : ${proof?.total ? `${proof.checked} heures sur ${proof.total} confirmées` : "non disponible"}`}
    className={`mr-2 inline-flex h-5 shrink-0 items-center rounded-[4px] border px-1.5 text-[10.5px] font-extrabold tabular-nums focus-visible:outline-2 focus-visible:outline-accent ${tone}`}>{label}</button>;
}

function TourHeader({ tour, open, onToggle, colSpan, currency, gps }) {
  const { time } = tour;
  const route = joined(time.route.count ? `route ${approx(time.route.estimated)}${timeLabel(time.route.seconds)}` : "", kmLabel(time.route.km));
  const onsite = time.loading.count || time.delivery.count ? `sur place ${approx(time.loading.estimated || time.delivery.estimated)}${timeLabel(time.loading.seconds + time.delivery.seconds)}` : "";
  const selected = Boolean(gps?.enabled) && gps.selected?.key === tour.key;
  return <tr className="bg-white">
    <td colSpan={colSpan} className={`border-t border-slate-200 p-0 ${selected ? "shadow-[inset_4px_0_0_var(--color-accent)]" : ""}`}><div className="flex items-center">
      <button type="button" aria-expanded={open} onClick={() => { onToggle(); gps?.select(tour.key); }}
        className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5 px-2 py-1 text-left text-xs hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-accent">
        {open ? <ChevronDown size={14} aria-hidden="true" className="shrink-0 text-slate-500" /> : <ChevronRight size={14} aria-hidden="true" className="shrink-0 text-slate-500" />}
        <span className="font-extrabold text-slate-900">{joined(tour.driver || "Chauffeur non indiqué", tour.truck)}</span>
        <span className="min-w-0 flex-1 text-slate-700">{tour.loads.join(", ") || "—"}{tour.clients.length ? <> → <strong>{tour.clients.join(", ")}</strong></> : null}
          <span className="ml-2 text-slate-500">{joined(tour.orders ? plural(tour.orders, "commande") : "", tonsLabel(tour.tons), route, onsite)}</span></span>
        <span className="whitespace-nowrap font-bold text-slate-700 tabular-nums">{hoursLabel(time.hours)}</span>
        <span className="w-24 whitespace-nowrap text-right font-extrabold text-slate-900 tabular-nums">{formatBillingMoney(tour.amount, currency)}</span>
      </button>
      {gps?.enabled ? <GpsChip proof={gps.proofs.get(tour.key)} selected={selected} onClick={() => gps.select(tour.key, true)} /> : null}
    </div></td>
  </tr>;
}

/** Table body: day and day sheet headers around the existing line rows (`renderRow`). */
export function InvoiceLineGroupRows({ view, colSpan, currency, renderRow, gps = null }) {
  if (!view.enabled) return view.other.map(({ row, index }) => renderRow(row, index));
  const filtered = Boolean(view.query.trim() || view.day);
  return <>
    {view.days.map((item) => <React.Fragment key={item.key}>
      <tr><td colSpan={colSpan} className="border-t border-slate-300 bg-slate-100 p-0 text-xs font-extrabold text-slate-800">
        <button type="button" aria-expanded={view.isDayOpen(item.key)} onClick={() => view.toggleDay(item.key)}
          className="flex w-full flex-wrap items-center gap-1 rounded-[4px] px-2 py-1 text-left hover:bg-slate-200 focus-visible:outline-2 focus-visible:outline-accent">
        {view.isDayOpen(item.key) ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        <span>{dayLabel(item.date, { weekday: "long", day: "numeric", month: "long" }).replace(/^./, (letter) => letter.toUpperCase())}</span>
        <span className="ml-2 font-semibold text-slate-500">{joined(plural(item.tourCount ?? item.tours.length, "tournée"), hoursLabel(item.hours), formatBillingMoney(item.amount, currency))}</span>
        </button>
      </td></tr>
      {view.isDayOpen(item.key) ? item.tours.map((tour) => <React.Fragment key={tour.key}>
        <TourHeader tour={tour} open={view.isOpen(tour.key)} onToggle={() => view.toggle(tour.key)} colSpan={colSpan} currency={currency} gps={gps} />
        {view.isOpen(tour.key) ? tour.rows.map(({ row, index }) => renderRow(row, index)) : null}
      </React.Fragment>) : null}
    </React.Fragment>)}
    {!view.days.length ? <tr><td colSpan={colSpan} className="border-t border-slate-200 px-2 py-2 text-xs text-slate-500">Aucune tournée ne correspond à la recherche.</td></tr> : null}
    {view.other.length && !filtered ? <>
      <tr><td colSpan={colSpan} className="border-t border-slate-300 bg-slate-100 px-2 py-1 text-xs font-extrabold text-slate-800">Autres lignes</td></tr>
      {view.other.map(({ row, index }) => renderRow(row, index))}
    </> : null}
  </>;
}
